import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Grid,
  FormControl,
  Select,
  MenuItem,
  Divider,
  Tooltip,
  IconButton,
  TextField,
  Avatar,
} from "@mui/material";

import ShareLink from "./ShareLink";
import ShareDialog from "./ShareDialog";
import ShareIcon from "@mui/icons-material/Share";
import QuestionMarkIcon from "@mui/icons-material/Help";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PrintAsPdf from "./PrintAsPdf";

import LoadingBackdrop from "./LoadingBackdrop";
import { BackButton } from "./functions";
import MoreToCome from "./MoreToCome";
import WelcomeDialogLessons from "./WelcomeDialogLessons";
import { AnimatedStep } from "./Animations";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";

import LoadingDialog from "./LoadingDialog";
import { useSearchParams, useParams } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import InfoBox from "./InfoBox";
import ImageIcon from "@mui/icons-material/Image";
import GenerateIcon from "@mui/icons-material/AutoAwesome";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import DateRangeIcon from "@mui/icons-material/DateRange";
import { handlePrintTwisters } from "./printFunctions";
import { callApi } from "./api";
import {
  generateAndProcessImage,
  trackLesson,
  CustomFormControlLabel,
  extractJsonFromString,
} from "./functions";
import { useSnackbar } from "./SnackbarHandler";
import ImageGenerationModal from "./ImageGenerationModal";
import PreviousLessonsLoader from "./PreviousLessonsLoader";
import PrintIcon from "@mui/icons-material/Print";
import ProgressBar from "./ProgressBar";
import Voice from "./Voice";
import LanguageSelector from "./LanguageSelector";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { FourSquare } from "react-loading-indicators";
import Logger from "./Logger";
import { Checkbox, FormControlLabel } from "@mui/material";
import StormIcon from "@mui/icons-material/Storm";

import HoverableImage from "./HoverableImage";
import LessonDrawer from "./LessonDrawer";
import SaveLessonDialog from "./SaveLessonDialog";
import SaveAsIcon from "@mui/icons-material/SaveAs";
import HistoryIcon from "@mui/icons-material/History";

import { db } from "./firebase";
import {
  collection,
  updateDoc,
  query,
  where,
  limit,
  serverTimestamp,
  addDoc,
  getDocs,
} from "firebase/firestore";

const TongueTwistersLesson = ({ theme, styles, user, setMinimized }) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);

  const [twisterCount, setTwisterCount] = useState(4);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isGeneratingImages, setIsGeneratingImages] = useState(false);
  const [lessonData, setLessonData] = useState(null);
  const [progress, setProgress] = useState(0);
  const { showSnackbar } = useSnackbar();
  const [showImageModal, setShowImageModal] = useState(false);
  const [imageInstructions, setImageInstructions] = useState("");
  const [selectedTwisterIndex, setSelectedTwisterIndex] = useState(null);
  const [loadingImageIndex, setLoadingImageIndex] = useState(null);
  const [language, setLanguage] = useState("svenska");
  const pdfButtonRef = useRef(null);
  const translateButtonRef = useRef(null);
  const shareLinkButtonRef = useRef(null);
  const [includeIPA, setIncludeIPA] = useState(true);
  const [includeSyllables, setIncludeSyllables] = useState(false);

  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [isEditingSubtitle, setIsEditingSubtitle] = useState(false);
  const [titleText, setTitleText] = useState("Tungvrickare");
  const [editingExampleId, setEditingExampleId] = useState(null);
  const [editingTwisterId, setEditingTwisterId] = useState(null);
  const [editingMeaningId, setEditingMeaningId] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const viewMode = searchParams.get("v") === "t";
  const [editingIPAId, setEditingIPAId] = useState(null);
  const [editingSyllableId, setEditingSyllableId] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);

  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareDialogOpenFriends, setShareDialogOpenFriends] = useState(false);
  const [isSharing, setIsSharing] = useState(false);

  useEffect(() => {
    if (lessonData) {
      setCurrentStep(2);
      setMinimized(true);
    } else {
      setMinimized(false);
    }
  }, [lessonData, currentStep]);

  const { lessonId } = useParams();
  useEffect(() => {
    const loadLessonFromId = async () => {
      if (lessonId) {
        try {
          const q = query(
            collection(db, "lesson_usage"),
            where("__name__", ">=", lessonId),
            where("__name__", "<=", lessonId + "\uf8ff"),
            where("lessonType", "==", "TongueTwistersLesson.js"),
            limit(1),
          );

          const querySnapshot = await getDocs(q);
          if (!querySnapshot.empty) {
            const docData = querySnapshot.docs[0].data();

            if (!docData.hasOpened) {
              await updateDoc(querySnapshot.docs[0].ref, {
                hasOpened: true,
                openedAt: Date.now(),
              });
            }

            setLessonData(docData.data);

            showSnackbar("Lektionen har laddats in.", "success");
          } else {
            showSnackbar("Kunde inte hitta lektionen", "error");
          }
        } catch (error) {
          console.error("Error loading lesson:", error);
          showSnackbar(
            "Ett fel uppstod när presentationen skulle laddas",
            "error",
          );
        }
      }
    };

    loadLessonFromId();
  }, lessonId);

  const [selectedModel, setSelectedModel] = useState("Studera.AI");
  const handleShare = async (
    lessonComment,
    lessonName,
    selectedImage,
    tags,
    shareData,
  ) => {
    setIsSharing(true);
    await new Promise((resolve) => setTimeout(resolve, 1500));

    try {
      // Create a document for each student and then create notifications
      const sharePromises = shareData.studentIds.map(async (studentId) => {
        const documentData = {
          userId: user.uid,
          shared: true,
          sharedTo: studentId,
          comment: shareData.comment,
          sharedAt: Date.now(),
          fileUrl: shareData.fileUrl,
          lessonType: "TongueTwistersLesson.js",
          timestamp: serverTimestamp(),
          lessonComment,
          lessonName,
          coverImage: selectedImage,
          hasPrinted: true,
          data: lessonData,
        };
        // Create lesson document and get ID
        const lessonDoc = await addDoc(
          collection(db, "lesson_usage"),
          documentData,
        );
        Logger.log(
          "Adding document for student",
          studentId,
          ":",
          JSON.stringify(documentData, null, 2),
        );
        // Create notification with lesson doc ID
        await addDoc(collection(db, "notifications"), {
          type: "lesson_shared",
          recipientId: studentId,
          senderId: user.uid,
          read: false,
          createdAt: serverTimestamp(),
          lessonData: {
            lessonId: lessonDoc.id,
            lessonType: documentData.lessonType,
            lessonName: documentData.lessonName,
            fileUrl: documentData.fileUrl,
            comment: documentData.comment,
            coverImage: selectedImage,
          },
        });
        return lessonDoc;
      });
      await Promise.all(sharePromises);
      showSnackbar(`Du har nu delat lektionen!`, "success");
    } catch (error) {
      showSnackbar("Fel vid delande av lektionen: " + error.message, "error");
      console.error(error);
    } finally {
      setIsSharing(false);
    }
  };

  const toggleViewMode = () => {
    const currentParams = Object.fromEntries(searchParams.entries());
    if (currentParams.v === "t") {
      delete currentParams.v;
    } else {
      currentParams.v = "t";
    }
    setSearchParams(currentParams);
  };

  const handleSaveLessonClick = () => {
    setSaveDialogOpen(true);
  };

  const handleSaveWithComment = async (comment, name, coverImage, tags) => {
    Logger.log(JSON.stringify(lessonData, null, 2));
    await trackLesson(
      user.uid,
      "TongueTwistersLesson.js",
      lessonData,
      null,
      true,
      comment,
      name,
      coverImage,
      tags,
    );
    setDrawerOpen(true);
    showSnackbar("Lektionen sparad i din lektionsbank!", "success");
  };

  const handlePrintClick = async () => {
    if (
      lessonData &&
      lessonData.twisters.every((twister) => twister.imageUrl)
    ) {
      Logger.log("Saving lesson since all images have been generated");
      await trackLesson(
        user.uid,
        "TongueTwistersLesson.js",
        lessonData,
        null,
        true,
        null,
        titleText,
      );
    }

    handlePrintTwisters(lessonData, theme, titleText, subtitleText);
  };

  const handleLoadPreviousLesson = (lessonData, formData) => {
    setLessonData(lessonData);
    Logger.log("lessonData: ", lessonData);
  };
  const getCurrentWeek = () => {
    const now = new Date();
    const onejan = new Date(now.getFullYear(), 0, 1);
    const week = Math.ceil(
      ((now.getTime() - onejan.getTime()) / 86400000 + onejan.getDay() + 1) / 7,
    );
    return week;
  };

  const [subtitleText, setSubtitleText] = useState(`Vecka ${getCurrentWeek()}`);

  const [isEditingStory, setIsEditingStory] = useState(false);

  const handleEditStory = (newText) => {
    setLessonData((prevData) => ({
      ...prevData,
      story: newText,
    }));
    setIsEditingStory(false);
    showSnackbar("Berättelsen har uppdaterats", "success");
  };

  const handleEditTwister = (index, newText, field) => {
    setLessonData((prevData) => ({
      ...prevData,
      twisters: prevData.twisters.map((twister, i) =>
        i === index ? { ...twister, [field]: newText } : twister,
      ),
    }));

    // Reset the appropriate editing state
    if (field === "twister") setEditingTwisterId(null);
    else if (field === "meaning") setEditingMeaningId(null);
    else if (field === "ipa_pronunciation") setEditingIPAId(null);
    else if (field === "syllable_pronunciation") setEditingSyllableId(null);

    showSnackbar(
      `${field === "twister" ? "Tungvrickaren" : field === "meaning" ? "Betydelsen" : "Uttalet"} har uppdaterats`,
      "success",
    );
  };

  const simulateProgress = (
    startProgress,
    endProgress,
    duration,
    setProgressFn,
  ) => {
    const stepSize = 0.5;
    const steps = (endProgress - startProgress) / stepSize;
    const stepDuration = duration / steps;
    let currentProgress = startProgress;

    const intervalId = setInterval(() => {
      currentProgress += stepSize;
      if (currentProgress >= endProgress) {
        clearInterval(intervalId);
      } else {
        setProgressFn(currentProgress);
      }
    }, stepDuration);

    return () => clearInterval(intervalId);
  };

  const handleGenerateTwisters = async () => {
    setIsGenerating(true);
    setProgress(0);
    let stopProgressSimulation = null;

    try {
      stopProgressSimulation = simulateProgress(0, 90, 15000, setProgress);

      const response = await callApi(
        [
          {
            role: "system",
            content: `Du är en språklärare som specialiserar sig på tungvrickare (så kallade tongue twisters) och uttal. VIKTIGT: Svara ENDAST med ett JSON-objekt, utan förklarande text före eller efter.`,
          },
          {
            role: "user",
            content: `Skapa en lektion med ${twisterCount} tungvrickare på ${language}. 
Returnera ENDAST ett JSON-objekt med följande struktur, utan någon text före eller efter:
{
  "title": "Tungvrickare",
  "instructions": "En kort instruktion om hur man ska träna på tungvrickarna",
  "twisters": [
    {
      "twister": "tungvrickaren",
      "meaning": "översättning och/eller förklaring av betydelsen på ${language}",
      ${includeIPA ? '"ipa_pronunciation": "IPA-notation"' : ""}${includeIPA && includeSyllables ? "," : ""}
      ${includeSyllables ? '"syllable_pronunciation": "ord-för-ord-med-bindestreck"' : ""}${includeIPA || includeSyllables ? "," : ""}
      "imagePrompt": "beskrivning för bildgenerering som illustrerar tungvrickaren",
      "imageUrl": null,
      "difficulty": "lätt/medel/svår"
    }
  ],
  "story": "En kort berättelse (cirka 12 meningar) som använder tungvrickarna på ett underhållande sätt"
}`,
          },
        ],
        8000,
        true,
        user.uid,
      );

      if (stopProgressSimulation) {
        stopProgressSimulation();
      }

      setProgress(95);

      let messageContent = response.data.choices[0].message.content.trim();
      if (messageContent.startsWith("```json")) {
        messageContent = messageContent.slice(7, -3).trim();
      } else if (messageContent.startsWith("```")) {
        messageContent = messageContent.slice(3, -3).trim();
      }
      const data = extractJsonFromString(messageContent);

      setProgress(100);
      await new Promise((resolve) => setTimeout(resolve, 800));

      setLessonData(data);
      Logger.log(JSON.stringify(data, null, 2));
      await trackLesson(user.uid, "TongueTwistersLesson.js", null, {
        twisterCount,
        language,
      });

      showSnackbar("Tungvrickare har genererats!", "success");
    } catch (error) {
      console.error("Error generating tongue twisters:", error);
      showSnackbar(
        "Ett fel uppstod när tungvrickarna skulle genereras",
        "error",
      );
    } finally {
      setIsGenerating(false);
    }
  };

  const handleImageButtonClick = () => {
    setShowImageModal(true);
  };

  const handleCloseModal = () => {
    setShowImageModal(false);
    setImageInstructions("");
    setSelectedTwisterIndex(null);
  };

  const handleImageUpdate = (index, newImageUrl) => {
    const updatedTwisters = [...lessonData.twisters];
    updatedTwisters[index] = {
      ...updatedTwisters[index],
      imageUrl: newImageUrl,
    };
    setLessonData({
      ...lessonData,
      twisters: updatedTwisters,
    });
  };

  const handleGenerateSpecificImage = async (index) => {
    setIsGeneratingImages(true);
    setLoadingImageIndex(index);
    handleCloseModal();

    try {
      const twister = lessonData.twisters[index];
      const imageUrl = await generateAndProcessImage(
        `${twister.imagePrompt}. ${imageInstructions}`,
        user.uid,
      );

      const updatedTwisters = [...lessonData.twisters];
      updatedTwisters[index] = { ...twister, imageUrl };

      setLessonData({
        ...lessonData,
        twisters: updatedTwisters,
      });

      showSnackbar("Bilden har genererats!", "success");
    } catch (error) {
      console.error("Error generating image:", error);
      showSnackbar("Ett fel uppstod när bilden skulle genereras", "error");
    } finally {
      setIsGeneratingImages(false);
      setLoadingImageIndex(null);
    }
  };

  const handleGenerateImages = async () => {
    if (!lessonData) return;

    setIsGeneratingImages(true);
    handleCloseModal();

    let successCount = 0;
    const totalImages = lessonData.twisters.length;

    try {
      const updatedTwisters = await Promise.all(
        lessonData.twisters.map(async (twister) => {
          try {
            const imageUrl = await generateAndProcessImage(
              `${twister.imagePrompt}. ${imageInstructions}`,
              user.uid,
            );
            successCount++;
            return { ...twister, imageUrl };
          } catch (error) {
            console.error(
              `Error generating image for ${twister.twister}:`,
              error,
            );
            return twister;
          }
        }),
      );

      setLessonData({
        ...lessonData,
        twisters: updatedTwisters,
      });

      if (successCount === totalImages) {
        showSnackbar("Alla bilder genererades framgångsrikt!", "success");
      } else {
        showSnackbar(
          `${successCount}/${totalImages} bilder genererades framgångsrikt.`,
          "warning",
        );
      }
    } finally {
      setIsGeneratingImages(false);
    }
  };

  return (
    <Box sx={styles.container}>
      <Box
        sx={{
          ...styles.innerContainer,
          width: { xs: "100%", md: "100%" },
          margin: "0 auto",
        }}
      >
        {!viewMode && (
          <>
            {currentStep !== 2 && (
              <Typography
                variant="h1"
                gutterBottom
                sx={{
                  ...styles.newH1,
                }}
              >
                Tungvrickare
              </Typography>
            )}
          </>
        )}
        <Box
          sx={{
            ...styles.buttonsContainer,
            ...styles.additionalButtonsContainer,
          }}
        >
          {!viewMode && (
            <>
              <Tooltip title="Dina sparade lektioner">
                <IconButton
                  onClick={() => setDrawerOpen(true)}
                  sx={{ ...styles.iconButtonBg }}
                >
                  <BookmarkBorderIcon />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
        {/*
        <InfoBox
          icon={StormIcon}
          title="Instruktioner för Tungvrickare"
          instructions={[
            "Välj antalet tungvrickare att generera",
            "Välj språk för tungvrickarna",
            "Välj fonetisk representation",
            "Generera illustrativa bilder",
          ]}
          theme={theme}
          styles={styles}
          user={user}
          lessonType="TongueTwistersLesson.js"
        />
*/}
        {currentStep === 1 && (
          <>
            <Box
              sx={{
                ...styles.freshBox,
                padding: "30px",
                width: { xs: "100%", md: "70%" },
                margin: "0 auto",
              }}
            >
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  {/*
              <PreviousLessonsLoader
                user={user}
                lessonType='TongueTwistersLesson.js'
                onLoadLesson={handleLoadPreviousLesson}
              />
*/}
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: "500", mb: 1 }}
                  >
                    Antal tungvrickare:
                  </Typography>
                  <FormControl fullWidth>
                    <Select
                      value={twisterCount}
                      onChange={(e) => setTwisterCount(e.target.value)}
                    >
                      {[2, 4, 6, 8, 10].map((num) => (
                        <MenuItem key={num} value={num}>
                          {num} tungvrickare
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: "500", mb: 1 }}
                  >
                    Språk:
                  </Typography>
                  <LanguageSelector
                    language={language}
                    setLanguage={setLanguage}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: "500", mb: 1 }}
                  >
                    Uttalsnotering:
                  </Typography>
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <CustomFormControlLabel
                      theme={theme}
                      control={<Checkbox />}
                      label={
                        <Box>
                          <Typography sx={{ fontSize: "0.975rem" }}>
                            IPA-notation
                          </Typography>
                        </Box>
                      }
                      checked={includeIPA}
                      onChange={(e) => setIncludeIPA(e.target.checked)}
                    />
                    <CustomFormControlLabel
                      theme={theme}
                      control={<Checkbox />}
                      label={
                        <Box>
                          <Typography sx={{ fontSize: "0.975rem" }}>
                            Ord-för-ord
                          </Typography>
                        </Box>
                      }
                      checked={includeSyllables}
                      onChange={(e) => setIncludeSyllables(e.target.checked)}
                    />
                  </Box>
                </Grid>
              </Grid>
              <Box sx={{ textAlign: "center" }}>
                <Button
                  variant={
                    theme.palette.mode === "light" ? "purp" : "contained"
                  }
                  onClick={handleGenerateTwisters}
                  disabled={isGenerating}
                  sx={{ mt: 6 }}
                  startIcon={
                    isGenerating ? (
                      <CircularProgress size={16} />
                    ) : (
                      <GenerateIcon />
                    )
                  }
                >
                  {isGenerating ? "Genererar..." : "Generera tungvrickare"}
                </Button>

                {isGenerating && (
                  <Box sx={{ mt: 7 }}>
                    <ProgressBar
                      progress={progress}
                      label="Genererar tungvrickare..."
                    />
                  </Box>
                )}
              </Box>
            </Box>
          </>
        )}

        <LessonDrawer
          userId={user.uid}
          lessonType="TongueTwistersLesson.js"
          theme={theme}
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          lessonIcon={StormIcon}
          onSelectLesson={(lesson) => {
            setLessonData(lesson.data);
            setTitleText(lesson.lessonName);
            setDrawerOpen(false);

            showSnackbar(`Lektionen har laddats in.`, "success");
            document
              .querySelector("hr")
              ?.scrollIntoView({ behavior: "smooth" });
          }}
        />
        {currentStep === 2 && (
          <>
            <BackButton
              currentStep={currentStep}
              onClick={() => {
                setCurrentStep(1);
                setLessonData(null);
              }}
              show={currentStep !== 1}
            />

            {lessonData && (
              <Box
                sx={{
                  display: "flex",
                  gap: 3,
                  flexDirection: {
                    xs: "column",
                    md: "row",
                  },
                  mt: 10,
                }}
              >
                <Box
                  sx={{
                    ...styles.freshBox,
                    padding: "30px",

                    ...(viewMode ? { flex: "0 0 70%" } : { flex: "1 1 auto" }),
                    margin: "0 auto",
                  }}
                >
                  <SaveLessonDialog
                    key={lessonData?.title}
                    open={saveDialogOpen}
                    onClose={() => setSaveDialogOpen(false)}
                    onSave={handleSaveWithComment}
                    theme={theme}
                    lessonObject={lessonData}
                    lessonName={titleText}
                  />

                  <ImageGenerationModal
                    open={showImageModal}
                    onClose={handleCloseModal}
                    onGenerate={
                      selectedTwisterIndex !== null
                        ? () =>
                            handleGenerateSpecificImage(selectedTwisterIndex)
                        : handleGenerateImages
                    }
                    isGenerating={isGeneratingImages}
                    instructions={imageInstructions}
                    setInstructions={setImageInstructions}
                    generationType="twisters"
                    theme={theme}
                  />

                  <Box sx={{ mt: 0 }}>
                    {/* Title */}
                    {isEditingTitle ? (
                      <TextField
                        value={titleText}
                        onChange={(e) => setTitleText(e.target.value)}
                        onBlur={() => setIsEditingTitle(false)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            setIsEditingTitle(false);
                          }
                        }}
                        autoFocus
                        fullWidth
                        variant="standard"
                        sx={{
                          backgroundColor: "transparent",
                          "& .MuiInputBase-input": {
                            textAlign: "center",
                            fontWeight: "600",
                            fontSize: "2.2em",
                            marginBottom: "1.2rem",
                            marginTop: "1.5rem",
                          },
                        }}
                        InputProps={{
                          disableUnderline: true,
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          position: "relative",
                          display: "inline-block",
                          margin: "0 auto",
                          width: "100%",
                          mb: 7,
                          mt: 0,
                          ":hover .edit-icon": {
                            opacity: 1,
                          },
                        }}
                      >
                        <Typography
                          variant="h1"
                          sx={{
                            fontWeight: "600",
                            fontSize: "2.2em",
                            cursor: "pointer",
                            textAlign: "center",
                            width: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 2,
                          }}
                        >
                          {titleText || " "}
                          <Box
                            component="span"
                            sx={{
                              opacity: 0,
                              transition: "opacity 0.2s",
                              display: "inline-flex",
                              gap: 0.5,
                            }}
                            className="edit-icon"
                          >
                            <Tooltip title="Redigera" placement="top">
                              <IconButton
                                size="small"
                                onClick={() => setIsEditingTitle(true)}
                              >
                                <EditIcon />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Typography>
                      </Box>
                    )}

                    {lessonData &&
                      lessonData.twisters.map((twister, index) => (
                        <Box
                          key={index}
                          sx={{
                            position: "relative",
                            display: "flex",
                            alignItems: "center",
                            mb: 5,
                            backgroundColor:
                              theme.palette.mode === "dark"
                                ? "#1e1e1e"
                                : "#f5f5f5",
                            padding: "20px",
                            borderRadius: "8px",
                            "&:hover": {
                              "& .delete-button": {
                                opacity: 1,
                                visibility: "visible",
                              },
                            },
                          }}
                        >
                          <IconButton
                            className="delete-button"
                            size="small"
                            onClick={() => {
                              const updatedTwisters =
                                lessonData.twisters.filter(
                                  (_, i) => i !== index,
                                );
                              setLessonData({
                                ...lessonData,
                                twisters: updatedTwisters,
                              });
                              showSnackbar(
                                "Tungvrickaren har tagits bort",
                                "success",
                              );
                            }}
                            sx={{
                              position: "absolute",
                              top: 8,
                              right: 8,
                              opacity: 0,
                              visibility: "hidden",
                              transition: "all 0.2s ease",
                              bgcolor:
                                theme.palette.mode === "dark"
                                  ? "rgba(0,0,0,0.2)"
                                  : "transparent",
                              "&:hover": {
                                bgcolor:
                                  theme.palette.mode === "dark"
                                    ? "rgba(0,0,0,0.3)"
                                    : "rgba(255,255,255,0.9)",
                              },
                            }}
                          >
                            <Tooltip
                              title="Ta bort tungvrickare"
                              placement="top"
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </Tooltip>
                          </IconButton>

                          <Box
                            sx={{
                              flex: "0 0 270px",
                              mr: 3,
                              position: "relative",
                            }}
                          >
                            <HoverableImage
                              imageUrl={twister.imageUrl}
                              isLoading={isGeneratingImages}
                              loadingIndex={loadingImageIndex}
                              currentIndex={index}
                              onGenerateClick={() => {
                                setSelectedTwisterIndex(index);
                                setShowImageModal(true);
                              }}
                              userId={user.uid}
                              onImageUpdate={(newImageUrl) =>
                                handleImageUpdate(index, newImageUrl)
                              }
                              height="270px"
                              theme={theme}
                              showSnackbar={showSnackbar}
                              tinyMode={false}
                            />
                          </Box>

                          <Box sx={{ flex: 1 }}>
                            {/* Tongue Twister */}
                            <Box
                              sx={{
                                "&:hover .controls": {
                                  opacity: 1,
                                },
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                mb: 1,
                              }}
                            >
                              {editingTwisterId === index ? (
                                <TextField
                                  fullWidth
                                  value={twister.twister}
                                  onChange={(e) => {
                                    const updatedTwisters = [
                                      ...lessonData.twisters,
                                    ];
                                    updatedTwisters[index] = {
                                      ...twister,
                                      twister: e.target.value,
                                    };
                                    setLessonData({
                                      ...lessonData,
                                      twisters: updatedTwisters,
                                    });
                                  }}
                                  onBlur={() =>
                                    handleEditTwister(
                                      index,
                                      twister.twister,
                                      "twister",
                                    )
                                  }
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" && !e.shiftKey) {
                                      e.preventDefault();
                                      handleEditTwister(
                                        index,
                                        twister.twister,
                                        "twister",
                                      );
                                    }
                                  }}
                                  autoFocus
                                  sx={{
                                    "& .MuiInputBase-input": {
                                      fontSize: "24px",
                                      fontWeight: "600",
                                    },
                                  }}
                                />
                              ) : (
                                <Typography
                                  variant="h2"
                                  sx={{
                                    fontSize: "24px",
                                    fontWeight: "600",
                                    flex: 1,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                  }}
                                >
                                  {twister.twister}
                                  <Box
                                    component="span"
                                    className="controls"
                                    sx={{
                                      opacity: 0,
                                      transition: "opacity 0.2s",
                                      display: "inline-flex",
                                      gap: 0.5,
                                      ml: 1,
                                    }}
                                  >
                                    <Voice
                                      content={twister.twister}
                                      fontSize={20}
                                      userId={user.uid}
                                      language={language}
                                      tooltipPlacement="top"
                                    />
                                    <Tooltip title="Redigera" placement="top">
                                      <IconButton
                                        size="small"
                                        onClick={() =>
                                          setEditingTwisterId(index)
                                        }
                                      >
                                        <EditIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  </Box>
                                </Typography>
                              )}
                            </Box>

                            {/* Pronunciation */}

                            {/* IPA Pronunciation */}
                            {includeIPA && twister.ipa_pronunciation && (
                              <Box
                                sx={{
                                  "&:hover .controls": {
                                    opacity: 1,
                                  },
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 1,
                                  mb: includeSyllables ? 1 : 0,
                                }}
                              >
                                {editingIPAId === index ? (
                                  <TextField
                                    fullWidth
                                    multiline
                                    value={twister.ipa_pronunciation}
                                    onChange={(e) => {
                                      const updatedTwisters = [
                                        ...lessonData.twisters,
                                      ];
                                      updatedTwisters[index] = {
                                        ...twister,
                                        ipa_pronunciation: e.target.value,
                                      };
                                      setLessonData({
                                        ...lessonData,
                                        twisters: updatedTwisters,
                                      });
                                    }}
                                    onBlur={() =>
                                      handleEditTwister(
                                        index,
                                        twister.ipa_pronunciation,
                                        "ipa_pronunciation",
                                      )
                                    }
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter" && !e.shiftKey) {
                                        e.preventDefault();
                                        handleEditTwister(
                                          index,
                                          twister.ipa_pronunciation,
                                          "ipa_pronunciation",
                                        );
                                      }
                                    }}
                                    autoFocus
                                  />
                                ) : (
                                  <Typography
                                    variant="body1"
                                    sx={{
                                      flex: 1,
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                      fontFamily: "monospace",
                                    }}
                                  >
                                    {twister.ipa_pronunciation}
                                    <Box
                                      component="span"
                                      className="controls"
                                      sx={{
                                        opacity: 0,
                                        transition: "opacity 0.2s",
                                        display: "inline-flex",
                                        gap: 0.5,
                                        ml: 1,
                                      }}
                                    >
                                      <Tooltip title="Redigera" placement="top">
                                        <IconButton
                                          size="small"
                                          onClick={() => setEditingIPAId(index)}
                                        >
                                          <EditIcon fontSize="small" />
                                        </IconButton>
                                      </Tooltip>
                                    </Box>
                                  </Typography>
                                )}
                              </Box>
                            )}

                            {includeSyllables &&
                              twister.syllable_pronunciation && (
                                <Box
                                  sx={{
                                    "&:hover .controls": {
                                      opacity: 1,
                                    },
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1,
                                  }}
                                >
                                  {editingSyllableId === index ? (
                                    <TextField
                                      fullWidth
                                      multiline
                                      value={twister.syllable_pronunciation}
                                      onChange={(e) => {
                                        const updatedTwisters = [
                                          ...lessonData.twisters,
                                        ];
                                        updatedTwisters[index] = {
                                          ...twister,
                                          syllable_pronunciation:
                                            e.target.value,
                                        };
                                        setLessonData({
                                          ...lessonData,
                                          twisters: updatedTwisters,
                                        });
                                      }}
                                      onBlur={() =>
                                        handleEditTwister(
                                          index,
                                          twister.syllable_pronunciation,
                                          "syllable_pronunciation",
                                        )
                                      }
                                      onKeyDown={(e) => {
                                        if (e.key === "Enter" && !e.shiftKey) {
                                          e.preventDefault();
                                          handleEditTwister(
                                            index,
                                            twister.syllable_pronunciation,
                                            "syllable_pronunciation",
                                          );
                                        }
                                      }}
                                      autoFocus
                                    />
                                  ) : (
                                    <Typography
                                      variant="body1"
                                      sx={{
                                        flex: 1,
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                        fontFamily: "monospace",
                                      }}
                                    >
                                      {twister.syllable_pronunciation}
                                      <Box
                                        component="span"
                                        className="controls"
                                        sx={{
                                          opacity: 0,
                                          transition: "opacity 0.2s",
                                          display: "inline-flex",
                                          gap: 0.5,
                                          ml: 1,
                                        }}
                                      >
                                        <Tooltip
                                          title="Redigera"
                                          placement="top"
                                        >
                                          <IconButton
                                            size="small"
                                            onClick={() =>
                                              setEditingSyllableId(index)
                                            }
                                          >
                                            <EditIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                      </Box>
                                    </Typography>
                                  )}
                                </Box>
                              )}
                          </Box>
                        </Box>
                      ))}

                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "center",
                        mt: 2,
                        mb: 4,
                      }}
                    >
                      <Button
                        variant={
                          theme.palette.mode === "light" ? "purp" : "contained"
                        }
                        startIcon={<AddIcon />}
                        onClick={() => {
                          const newTwister = {
                            twister: "Ny tungvrickare",
                            meaning: "Skriv betydelsen här",
                            ipa_pronunciation: "Skriv IPA-uttalet här",
                            syllable_pronunciation:
                              "Ord-för-ord-med-bindestreck här",
                            imagePrompt: "",
                            imageUrl: null,
                            difficulty: "medel",
                          };
                          setLessonData({
                            ...lessonData,
                            twisters: [...lessonData.twisters, newTwister],
                          });
                          showSnackbar(
                            "Ny tungvrickare har lagts till",
                            "success",
                          );
                        }}
                        sx={{
                          mt: 2,
                          mb: 4,
                        }}
                      >
                        Lägg till tungvrickare
                      </Button>
                    </Box>

                    <Box
                      sx={{
                        mt: 8,
                        mb: 4,
                        padding: "30px",
                        backgroundColor:
                          theme.palette.mode === "dark" ? "#1e1e1e" : "#f5f5f5",
                        borderRadius: "8px",
                      }}
                    >
                      <Typography
                        variant="h2"
                        sx={{ fontSize: "1.5em", mb: 3, fontWeight: "600" }}
                      >
                        Berättelse med tungvrickare
                      </Typography>
                      <Box
                        sx={{
                          "&:hover .controls": {
                            opacity: 1,
                          },
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 1,
                        }}
                      >
                        {isEditingStory ? (
                          <TextField
                            fullWidth
                            multiline
                            rows={10}
                            defaultValue={lessonData.story}
                            onBlur={(e) => handleEditStory(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" && !e.shiftKey) {
                                e.preventDefault();
                                handleEditStory(e.target.value);
                              }
                            }}
                            autoFocus
                            sx={{
                              "& .MuiInputBase-input": {
                                lineHeight: 1.8,
                              },
                            }}
                          />
                        ) : (
                          <Typography
                            variant="body1"
                            sx={{
                              lineHeight: 1.8,
                              flex: 1,
                              display: "flex",
                              alignItems: "flex-start",
                              gap: 1,
                            }}
                          >
                            {lessonData.story || " "}
                            <Box
                              component="span"
                              className="controls"
                              sx={{
                                opacity: 0,
                                transition: "opacity 0.2s",
                                display: "inline-flex",
                                gap: 0.5,
                                ml: 1,
                                mt: 0.5,
                              }}
                            >
                              <Voice
                                content={lessonData.story}
                                fontSize={20}
                                userId={user.uid}
                                language={language}
                                tooltipPlacement="top"
                              />
                              <Tooltip title="Redigera" placement="top">
                                <IconButton
                                  size="small"
                                  onClick={() => setIsEditingStory(true)}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </Box>
                </Box>

                {!viewMode && (
                  <Box sx={{ position: "relative" }}>
                    <Box
                      sx={{
                        ...styles.lessonControlPanelNuevo2,

                        "& .MuiButton-root": {
                          ...styles.lessonControlPanelNewButtons2,
                        },
                      }}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 600,
                          color: "text.secondary",
                        }}
                      >
                        Hantera
                      </Typography>
                      <Button
                        variant={
                          theme.palette.mode === "light" ? "purp" : "contained"
                        }
                        onClick={handleImageButtonClick}
                        disabled={isGeneratingImages}
                        sx={{ mr: 2 }}
                        startIcon={
                          isGeneratingImages ? (
                            <CircularProgress size={16} />
                          ) : (
                            <ImageIcon />
                          )
                        }
                      >
                        {isGeneratingImages
                          ? "Genererar bilder..."
                          : "Generera bilder"}
                      </Button>

                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 600,
                          color: "text.secondary",
                          mt: 3,
                        }}
                      >
                        Spara
                      </Typography>
                      <Button
                        variant="print"
                        startIcon={<SaveAsIcon />}
                        sx={{ ml: 2 }}
                        onClick={handleSaveLessonClick}
                      >
                        Spara i lektionsbank
                      </Button>
                      <Button
                        variant="print"
                        onClick={handlePrintClick}
                        startIcon={<PrintIcon />}
                      >
                        Skriv ut
                      </Button>
                      <PrintAsPdf
                        buttonRef={pdfButtonRef}
                        onPrint={handlePrintClick}
                        sx={{ ml: 2 }}
                      />

                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 600,
                          color: "text.secondary",
                          mt: 3,
                        }}
                      >
                        Dela
                      </Typography>
                      <ShareLink
                        buttonRef={shareLinkButtonRef}
                        key={lessonId}
                        lessonId={lessonId}
                        lessonData={lessonData}
                        user={user}
                        lessonType={"TongueTwistersLesson.js"}
                        onCopy={(message, severity) =>
                          showSnackbar(message, severity)
                        }
                      />
                      <Button
                        variant="success"
                        sx={{}}
                        onClick={() => setShareDialogOpenFriends(true)}
                        startIcon={<ShareIcon />}
                      >
                        Dela med vänner
                      </Button>
                      <Button
                        variant="success"
                        sx={{
                          mt: 2,
                          ml: 2,
                        }}
                        onClick={() => setShareDialogOpen(true)}
                        startIcon={<ShareIcon />}
                      >
                        Dela med elever
                      </Button>

                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 600,
                          color: "text.secondary",
                          mt: 3,
                        }}
                      >
                        Visa
                      </Typography>
                      <Tooltip title="Hur lektionen ser ut för eleverna">
                        <Button
                          variant="success"
                          onClick={() => {
                            window.scrollTo({ top: 0, behavior: "smooth" });
                            toggleViewMode();
                          }}
                          startIcon={
                            searchParams.get("v") === "t" ? (
                              <PersonOutlineIcon />
                            ) : (
                              <PersonOutlineIcon />
                            )
                          }
                        >
                          {searchParams.get("v") === "t"
                            ? "Redigeringsläge"
                            : "Elevläge"}
                        </Button>
                      </Tooltip>
                      <Box>
                        <MoreToCome user={user} />
                      </Box>
                    </Box>
                  </Box>
                )}
              </Box>
            )}
          </>
        )}

        <ShareDialog
          open={shareDialogOpen}
          onClose={() => setShareDialogOpen(false)}
          onShare={handleShare}
          user={user}
          isLessonSharing={true}
          theme={theme}
          lessonName={lessonData?.title || ""}
          lessonObject={lessonData}
          lessonType={"TongueTwistersLesson.js"}
        />

        <ShareDialog
          open={shareDialogOpenFriends}
          onClose={() => setShareDialogOpenFriends(false)}
          onShare={handleShare}
          user={user}
          isLessonSharing={true}
          theme={theme}
          lessonName={lessonData?.title || ""}
          lessonObject={lessonData}
          lessonType={"TongueTwistersLesson.js"}
          shareWithFriends={true}
        />

        <LoadingBackdrop
          isOpen={isSharing}
          imagePath="/img/AI.png"
          loadingText={"Delar lektionen..."}
        />

        <LoadingDialog
          open={isGenerating}
          onClose={() => {}}
          progress={progress}
          progressLabel={"Genererar lektion — tar 30-60 sekunder..."}
          title="Genererar lektion. Snart klart..."
          snackbarMessage="Genereringen är snart klar. Vänligen vänta."
          snackbarSeverity="info"
          progressBarHeight="15px"
          progressBarFontSize="0.9rem"
          coffee={false}
        />

        <LoadingDialog
          open={isGeneratingImages}
          onClose={() => {}}
          progress={progress}
          progressLabel={"Genererar bilderna — tar 30-60 sekunder..."}
          hideProgressBar={true}
          title="Genererar bilderna. Snart klart..."
          snackbarMessage="Genereringen är snart klar. Vänligen vänta."
          snackbarSeverity="info"
          progressBarHeight="15px"
          progressBarFontSize="0.9rem"
          coffee={false}
        />
      </Box>
    </Box>
  );
};

export default TongueTwistersLesson;
